import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { overviewUrl, overviewBranchesUrl, overviewActivityUrl } from '../../BaseURL';
import { fmtMoney, fmtDate } from '../../utils/format';

const fmt = (n) => Number(n ?? 0).toLocaleString();

const Overview = ({ facility, me }) => {
  const isSuper = me?.role === 'super_admin';
  const [selectedBranch, setSelectedBranch] = useState('all'); // 'all' or branch.id
  const [stats, setStats] = useState(null);
  const [branchRows, setBranchRows] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = async (branchScope) => {
    setLoading(true);
    try {
      const branchParam = (isSuper && branchScope !== 'all') ? { branch_id: branchScope } : {};
      const tasks = [
        axios.get(overviewUrl, { params: branchParam }),
        axios.get(overviewActivityUrl, { params: { ...branchParam, limit: 10 } }),
      ];
      if (isSuper) tasks.push(axios.get(overviewBranchesUrl));
      const [overview, recent, branches] = await Promise.all(tasks);
      if (overview.data?.success) setStats(overview.data.stats);
      if (recent.data?.success) setActivity(recent.data.results || []);
      if (branches?.data?.success) setBranchRows(branches.data.results || []);
    } catch (_) {} finally { setLoading(false); }
  };

  useEffect(() => { loadAll(selectedBranch); /* eslint-disable-next-line */ }, [selectedBranch]);

  const selectedBranchObj = useMemo(
    () => branchRows.find((b) => b.id === Number(selectedBranch)),
    [branchRows, selectedBranch]
  );

  const cards = [
    { lbl: 'Sales today',     val: fmt(stats?.sales_today), hint: 'transactions',       ic: '🧾', tone: 'brand' },
    { lbl: 'Revenue today',   val: fmtMoney(stats?.revenue_today), hint: 'gross sales', ic: '💵', tone: 'success' },
    { lbl: 'Profit today',    val: fmtMoney(stats?.profit_today),  hint: 'after COGS',  ic: '📈', tone: 'accent' },
    { lbl: 'Low stock',       val: fmt(stats?.low_stock_alerts),   hint: 'at or below reorder', ic: '⚠️', tone: 'warn' },
  ];
  if (isSuper && selectedBranch === 'all') {
    cards.push({ lbl: 'Branches', val: fmt(stats?.active_branches), hint: 'active', ic: '🏬', tone: 'brand' });
  }
  cards.push({ lbl: isSuper && selectedBranch === 'all' ? 'Staff' : 'Branch staff', val: fmt(stats?.active_users), hint: 'active accounts', ic: '👥', tone: 'mute' });

  const scopeLabel = isSuper
    ? (selectedBranch === 'all' ? 'All branches' : (selectedBranchObj?.name || 'Branch'))
    : (selectedBranchObj?.name || 'Your branch');

  return (
    <>
      {/* HERO */}
      <motion.div
        className="ov-hero"
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
      >
        <div className="ov-hero-text">
          <h2>Welcome back, {me?.full_name?.split(' ')[0] || 'there'} 👋</h2>
          <p>
            {facility?.name ? <>You're managing <strong>{facility.name}</strong></> : 'Welcome to your dashboard'}
            {facility?.business_type ? <> · <span style={{ textTransform: 'capitalize' }}>{String(facility.business_type).replace('_', ' ')}</span></> : null}
            {' · '}<span className="muted">Viewing {scopeLabel}</span>
          </p>
        </div>
        <div className="ov-hero-side">
          <div className="ov-pill">{new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })}</div>
        </div>
      </motion.div>

      {/* BRANCH PICKER (super admin only) */}
      {isSuper && branchRows.length > 0 && (
        <div className="ov-branches-chips">
          <button
            className={`ov-chip ${selectedBranch === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedBranch('all')}
          >
            <span className="ic">🏢</span>
            All branches
            <span className="ct">{branchRows.length}</span>
          </button>
          {branchRows.map((b) => (
            <button
              key={b.id}
              className={`ov-chip ${Number(selectedBranch) === b.id ? 'active' : ''}`}
              onClick={() => setSelectedBranch(b.id)}
              title={b.manager_name ? `Manager: ${b.manager_name}` : 'No manager'}
            >
              <span className="ic">{b.is_head_office ? '🏛️' : '🏬'}</span>
              {b.name}
              {b.is_head_office ? <span className="hq-tag">HQ</span> : null}
            </button>
          ))}
        </div>
      )}

      {/* KPI CARDS */}
      <div className="ov-kpi">
        {cards.map((c, i) => (
          <motion.div
            key={c.lbl}
            className={`ov-stat tone-${c.tone}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: i * 0.04 }}
          >
            <div className="ic">{c.ic}</div>
            <div className="lbl">{c.lbl}</div>
            <div className="val">{loading ? '—' : c.val}</div>
            <div className="hint">{c.hint}</div>
          </motion.div>
        ))}
      </div>

      {/* TWO-COLUMN: Branch grid + recent activity, or selected branch detail + activity */}
      <div className="ov-grid">
        {/* LEFT */}
        <div>
          {isSuper && selectedBranch === 'all' && branchRows.length > 0 && (
            <div className="panel">
              <div className="panel-head">
                <h2>Branch performance — today</h2>
                <span className="muted-sm">Click any branch to drill in</span>
              </div>
              <div className="ov-branch-grid">
                <AnimatePresence>
                  {branchRows.map((b, i) => (
                    <motion.button
                      key={b.id}
                      className="ov-branch-card"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.03 }}
                      whileHover={{ y: -3 }}
                      onClick={() => setSelectedBranch(b.id)}
                    >
                      <div className="ov-branch-head">
                        <div>
                          <div className="code">
                            {b.is_head_office ? <span className="hq-tag">HQ</span> : null}
                            {b.branch_code}
                          </div>
                          <h3>{b.name}</h3>
                          <div className="region">{b.region || 'No region set'}</div>
                        </div>
                        <div className="staff-pill">{b.staff_count} staff</div>
                      </div>
                      <div className="ov-branch-stats">
                        <div>
                          <div className="lbl">Revenue</div>
                          <div className="val">{fmtMoney(b.revenue_today)}</div>
                        </div>
                        <div>
                          <div className="lbl">Sales</div>
                          <div className="val">{b.sales_today}</div>
                        </div>
                        <div>
                          <div className="lbl">Low stock</div>
                          <div className={`val ${Number(b.low_stock) > 0 ? 'warn' : ''}`}>{b.low_stock}</div>
                        </div>
                      </div>
                      <div className="ov-branch-foot">
                        <span className="mgr">👤 {b.manager_name || <em className="muted">No manager</em>}</span>
                        <span className="arrow">→</span>
                      </div>
                    </motion.button>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          )}

          {isSuper && selectedBranch !== 'all' && selectedBranchObj && (
            <div className="panel">
              <div className="panel-head">
                <h2>{selectedBranchObj.name}</h2>
                <button className="btn-link" onClick={() => setSelectedBranch('all')}>← Back to all branches</button>
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', color: 'var(--mute)', fontSize: 14 }}>
                <span><strong style={{ color: 'var(--ink)' }}>{selectedBranchObj.branch_code}</strong> · {selectedBranchObj.region || 'No region'}</span>
                <span>👤 Manager: <strong style={{ color: 'var(--ink)' }}>{selectedBranchObj.manager_name || 'Unassigned'}</strong></span>
                <span>👥 {selectedBranchObj.staff_count} active staff</span>
              </div>
            </div>
          )}

          {!isSuper && (
            <div className="panel">
              <div className="panel-head"><h2>Quick links</h2></div>
              <div className="ov-links">
                <a href="#/management/pos">🛒 Open the POS</a>
                <a href="#/management/sales">💵 Today's sales</a>
                <a href="#/management/inventory">📦 Inventory</a>
                <a href="#/management/products">🏷️ Products</a>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT — Recent activity */}
        <div className="panel">
          <div className="panel-head">
            <h2>Recent activity</h2>
            <a href="#/management/audit" className="btn-link">Full audit trail →</a>
          </div>
          {activity.length === 0 ? (
            <p style={{ color: 'var(--mute)', margin: 0 }}>No activity yet. Sales and admin actions will show here.</p>
          ) : (
            <ul className="ov-activity">
              {activity.map((a) => (
                <li key={a.id} className="ov-act">
                  <span className={`act-dot cat-${a.category || 'other'}`} />
                  <div className="act-body">
                    <div className="msg">{a.message || a.action_type}</div>
                    <div className="meta">
                      {a.actor_name || 'System'}
                      {a.branch_name ? <> · {a.branch_name}</> : null}
                      {' · '}{fmtDate(a.created_at)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
};

export default Overview;
